import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wallet, CheckCircle2, Play, Square, AlertTriangle, Flag, Clock, Smartphone } from "lucide-react";
import { api, errMsg, formatIDR, formatDuration, formatTime } from "@/lib/api";
import { Avatar, StatusBadge, Countdown, VerifiedBadge } from "@/components/shared";
import { AvailabilityEditor } from "@/components/AvailabilityEditor";
import { DaysOffEditor } from "@/components/DaysOffEditor";
import { EducationEditor } from "@/components/EducationEditor";
import { VideoEditor } from "@/components/VideoEditor";
import { ProfileEditor } from "@/components/ProfileEditor";
import { PayoutHistory } from "@/components/PayoutHistory";

const CRISIS_SCRIPT = [
  "Tetap tenang, dengarkan tanpa menghakimi, jangan menjanjikan kerahasiaan mutlak.",
  "Tanyakan langsung dan lembut apakah ia sedang memikirkan untuk menyakiti diri.",
  "Berikan hotline resmi: 112 (darurat) dan Kemenkes 119 ext. 8. Dorong untuk menghubungi orang terdekat.",
  "Tekan tombol 'Tandai Krisis' agar admin segera menindaklanjuti.",
];

function OrderItem({ o, act }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("Pelecehan / konten seksual");
  const [endSession, setEndSession] = useState(true);
  return (
    <div className={`card-soft p-5 ${o.crisis_flag ? "border-rose-300" : ""}`} data-testid={`talent-order-${o.code}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-heading font-semibold text-slate-800 tracking-wide">{o.code}</p>
          <p className="text-sm text-slate-500">{o.service_name} · {formatDuration(o.duration_minutes)} · {formatIDR(o.price)}</p>
        </div>
        <StatusBadge status={o.status} />
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-y-2 text-sm">
        <dt className="text-slate-500">User</dt><dd className="text-slate-800">{o.nickname}</dd>
        <dt className="text-slate-500">WhatsApp</dt><dd className="text-slate-800 font-medium" data-testid={`talent-order-wa-${o.code}`}>{["accepted", "in_session"].includes(o.status) ? `+${o.whatsapp}` : o.whatsapp}</dd>
        {o.notes && <><dt className="text-slate-500">Catatan</dt><dd className="text-slate-700">{o.notes}</dd></>}
        <dt className="text-slate-500">Jadwal</dt><dd className="text-slate-800 font-medium" data-testid={`talent-order-schedule-${o.code}`}>{o.is_immediate ? "Sesegera mungkin" : `${formatTime(o.scheduled_at)} WIB`}</dd>
        <dt className="text-slate-500">Update</dt><dd className="text-slate-700">{formatTime(o.updated_at)}</dd>
      </dl>
      {o.crisis_flag && <p className="mt-3 text-xs text-rose-700 inline-flex items-center gap-1"><AlertTriangle size={13} strokeWidth={1.5} />Ditandai krisis, admin diberi tahu.</p>}

      {o.status === "paid" && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-600 inline-flex items-center gap-1"><Clock size={14} strokeWidth={1.5} />Sisa waktu terima: <Countdown target={o.accept_deadline} onDone={() => act("refresh")} /></span>
          <div className="ml-auto flex gap-2">
            <button className="btn-outline h-10 px-4" onClick={() => act("reject", o)} data-testid={`talent-reject-${o.code}`}>Tolak</button>
            <button className="btn-primary h-10 px-4" onClick={() => act("accept", o)} data-testid={`talent-accept-${o.code}`}><CheckCircle2 size={16} strokeWidth={1.5} />Terima</button>
          </div>
        </div>
      )}
      {o.status === "accepted" && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-slate-600">Hubungi user dari nomor resmi Sudut Nadi, sebut kode {o.code}, lalu mulai sesi.</p>
          <button className="btn-primary h-10 px-4" onClick={() => act("start", o)} data-testid={`talent-start-${o.code}`}><Play size={16} strokeWidth={1.5} />Mulai sesi</button>
        </div>
      )}
      {o.status === "in_session" && (
        <div className="mt-4 rounded-lg bg-secondary px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div><p className="text-xs text-slate-500">Sisa waktu sesi</p><Countdown target={o.session_ends_at} className="text-2xl text-slate-800" /></div>
          <button className="btn-primary h-10 px-4" onClick={() => act("complete", o)} data-testid={`talent-complete-${o.code}`}><Square size={16} strokeWidth={1.5} />Selesai</button>
        </div>
      )}
      {["accepted", "in_session"].includes(o.status) && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {!o.crisis_flag && <button className="btn-ghost text-rose-700 hover:bg-rose-50 h-9 text-sm" onClick={() => act("crisis", o)} data-testid={`talent-crisis-${o.code}`}><AlertTriangle size={15} strokeWidth={1.5} />Tandai Krisis</button>}
          <button className="btn-ghost h-9 text-sm" onClick={() => setReportOpen(!reportOpen)} data-testid={`talent-report-toggle-${o.code}`}><Flag size={15} strokeWidth={1.5} />Laporkan user</button>
        </div>
      )}
      {reportOpen && (
        <div className="mt-3 rounded-lg border border-border p-4 space-y-3" data-testid={`talent-report-form-${o.code}`}>
          <select className="input-soft h-10" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="talent-report-reason">
            {["Pelecehan / konten seksual", "Diduga di bawah umur", "Minta transaksi di luar platform", "Lainnya"].map((r) => <option key={r}>{r}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={endSession} onChange={(e) => setEndSession(e.target.checked)} className="accent-[#6B8E7B]" data-testid="talent-report-end-session" />Hentikan sesi sekarang</label>
          <button className="btn-primary h-10 px-4" onClick={() => { act("report", o, { reason, end_session: endSession }); setReportOpen(false); }} data-testid="talent-report-submit">Kirim laporan</button>
        </div>
      )}
    </div>
  );
}

export default function TalentDashboard() {
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("masuk");

  const load = useCallback(async () => {
    try {
      const [m, o] = await Promise.all([api.get("/talent/me"), api.get("/talent/orders")]);
      setMe(m.data); setOrders(o.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [load]);

  const act = async (type, o, extra = {}) => {
    if (type === "refresh") return load();
    try {
      if (type === "crisis") await api.post(`/talent/orders/${o.id}/flag-crisis`, { note: "Ditandai dari dashboard" });
      else if (type === "report") await api.post(`/talent/orders/${o.id}/report`, { ...extra, details: "" });
      else await api.post(`/talent/orders/${o.id}/${type}`);
      toast.success({ accept: "Order diterima. Segera hubungi user.", reject: "Order ditolak dan dialihkan.", start: "Sesi dimulai.", complete: "Sesi selesai. Komisi masuk ke saldo.", crisis: "Admin diberi tahu.", report: "Laporan terkirim." }[type]);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleOnline = async () => {
    try { const { data } = await api.patch("/talent/me", { is_online: !me.is_online }); setMe((m) => ({ ...m, ...data })); } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadKtp = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Ukuran KTP maksimal 5 MB");
    const fd = new FormData();
    fd.append("ktp", file);
    try { const { data } = await api.post("/talent/me/ktp", fd, { headers: { "Content-Type": "multipart/form-data" } }); setMe((m) => ({ ...m, ktp_uploaded_at: data.ktp_uploaded_at })); toast.success("KTP terunggah ke penyimpanan privat"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!me) return <div className="py-24 text-center text-slate-500">Memuat dashboard...</div>;

  const groups = {
    masuk: orders.filter((o) => o.status === "paid"),
    aktif: orders.filter((o) => ["accepted", "in_session"].includes(o.status)),
    riwayat: orders.filter((o) => !["paid", "accepted", "in_session"].includes(o.status)),
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="talent-dashboard">
      <div className="grid gap-6 md:grid-cols-[1.2fr_1fr]">
        <div className="card-soft p-6 flex gap-4">
          <Avatar src={me.avatar_url} name={me.name} size={64} online={me.is_online} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap"><h1 className="font-heading text-2xl font-medium text-slate-800">{me.name}</h1>{me.status === "approved" && <VerifiedBadge />}</div>
            <p className="text-sm text-slate-500 mt-1">{me.schedule || "Jadwal belum diisi"}</p>
            {me.status === "approved" ? (
              <button onClick={toggleOnline} className={`mt-4 h-10 px-4 rounded-full text-sm font-medium border transition-colors duration-300 ${me.is_online ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-card border-border text-slate-600"}`} data-testid="talent-online-toggle">
                {me.is_online ? "Online — menerima order" : "Offline — klik untuk online"}
              </button>
            ) : (
              <p className="mt-4 text-sm rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900" data-testid="talent-pending-note">Status akun: {me.status === "pending" ? "menunggu persetujuan admin" : me.status}. Kamu belum bisa menerima order.</p>
            )}
            {me.status !== "banned" && (
              <div className={`mt-4 rounded-lg border px-3 py-2.5 text-sm ${me.whatsapp_business ? "bg-sky-50 border-sky-200 text-sky-900" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="talent-official-wa">
                <p className="font-medium inline-flex items-center gap-1.5"><Smartphone size={14} strokeWidth={1.5} />Nomor WA resmi Sudut Nadi</p>
                {me.whatsapp_business
                  ? <p className="mt-1"><span className="font-heading font-semibold tracking-wide" data-testid="talent-official-wa-number">+{me.whatsapp_business}</span> · hubungi user hanya dari nomor ini. HP kamu tertaut sebagai perangkat pendamping; akses dapat dicabut admin kapan saja.</p>
                  : <p className="mt-1" data-testid="talent-official-wa-missing">Belum ditetapkan admin. Kamu belum bisa online atau menerima order sampai nomor resmi diberikan{me.status === "pending" ? " setelah wawancara" : ""}.</p>}
              </div>
            )}
            {me.status !== "banned" && (
              <div className="mt-4 text-xs text-slate-500" data-testid="talent-ktp-section">
                <span data-testid="talent-ktp-status">{me.ktp_uploaded_at ? `KTP terunggah ${new Date(me.ktp_uploaded_at).toLocaleDateString("id-ID")} (privat, hanya admin)` : "KTP belum diunggah"}</span>
                <label className="ml-2 underline cursor-pointer text-primary">{me.ktp_uploaded_at ? "Unggah ulang" : "Unggah KTP"}
                  <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={uploadKtp} data-testid="talent-ktp-file" />
                </label>
              </div>
            )}
          </div>
        </div>
        <div className="card-soft p-6">
          <p className="eyebrow inline-flex items-center gap-2"><Wallet size={14} strokeWidth={1.5} />Saldo komisi</p>
          <p className="mt-2 font-heading text-3xl font-semibold text-slate-800" data-testid="talent-balance">{formatIDR(me.balance?.balance)}</p>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-slate-600">
            <div><p className="text-slate-400 text-xs">Total komisi</p><p>{formatIDR(me.balance?.earned)}</p></div>
            <div><p className="text-slate-400 text-xs">Sudah dibayar</p><p>{formatIDR(me.balance?.paid_out)}</p></div>
            <div><p className="text-slate-400 text-xs">Sesi selesai</p><p>{me.completed_count}</p></div>
            <div><p className="text-slate-400 text-xs">Rating</p><p>{me.rating_avg?.toFixed(1)} ({me.rating_count})</p></div>
          </div>
          <PayoutHistory key={me.balance?.paid_out} />
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <ProfileEditor key={me.id} me={me} onSaved={(data) => setMe((m) => ({ ...m, ...data }))} />
        <AvailabilityEditor key={JSON.stringify(me.availability || {})} availability={me.availability} onSaved={(data) => setMe((m) => ({ ...m, ...data }))} />
        <DaysOffEditor daysOff={me.days_off || []} onSaved={(data) => setMe((m) => ({ ...m, ...data }))} />
        <EducationEditor education={me.education || []} onSaved={(data) => setMe((m) => ({ ...m, ...data }))} />
        <VideoEditor talentId={me.id} hasVideo={me.has_video} uploadedAt={me.video_uploaded_at} onSaved={(data) => setMe((m) => ({ ...m, has_video: data.has_video, video_uploaded_at: data.video_uploaded_at }))} />
        <details className="card-soft p-5" data-testid="crisis-protocol">
        <summary className="cursor-pointer font-heading font-medium text-slate-800 inline-flex items-center gap-2"><AlertTriangle size={16} strokeWidth={1.5} className="text-rose-600" />Skrip protokol krisis</summary>
        <ol className="mt-3 list-decimal pl-5 space-y-1.5 text-sm text-slate-600">{CRISIS_SCRIPT.map((s) => <li key={s}>{s}</li>)}</ol>
      </details>
      </div>
      {!me.availability || Object.keys(me.availability).length === 0 ? <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="availability-empty-note">Jadwal ketersediaan belum diisi. User belum bisa memesan kamu sampai jadwal diisi.</p> : null}

      <div className="mt-10 flex gap-2 flex-wrap" data-testid="talent-tabs">
        {[["masuk", "Order Masuk"], ["aktif", "Sesi Aktif"], ["riwayat", "Riwayat"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 h-10 rounded-full text-sm border transition-colors duration-300 ${tab === k ? "bg-primary text-white border-primary" : "bg-card border-border text-slate-600 hover:bg-secondary"}`} data-testid={`talent-tab-${k}`}>{label} ({groups[k].length})</button>
        ))}
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid={`talent-orders-${tab}`}>
        {groups[tab].length === 0 && <p className="text-slate-500 text-sm" data-testid="talent-orders-empty">Belum ada order di sini.</p>}
        {groups[tab].map((o) => <OrderItem key={o.id} o={o} act={act} />)}
      </div>
    </div>
  );
}
